import { TAG_HABITATS, tagPlaceOn, type GroupRecord, type TagCause, type TagHabitat, type TagPlace, type TagRecord, type TagSpecies } from '../sim/tags';
import type { World } from '../sim/types';
import { dateLabel } from '../sim/calendar';
import { escapeHtml, whole } from './format';
import { SPECIES_NAMES } from './wildlife';
import { displayName, sentenceName, speciesOf } from '../sim/species';

/**
 * The Tagged list (M148, the user's choices: "A Tagged list", "Marked in the views", "On the world map"): each tagged
 * animal by its name, its kind, its island, its age, how long it has been followed and whether it lives (how and when
 * it died if not), and its tag and price as read; each named school by its name and its island's grown fish today. Its
 * rows are the simulation's read-out (`tagRecords`), nothing written.
 */

/** The kinds' names as the player reads them: the reef's the follow card's (`SPECIES_NAMES`), the others the registry's (M160). */
const TAG_SPECIES_NAMES: Readonly<Record<TagSpecies, string>> = {
  ...SPECIES_NAMES,
  honu: displayName(speciesOf('honu')),
  honuea: displayName(speciesOf('honuea')),
  monkSeal: displayName(speciesOf('monkSeal')),
  tigerShark: displayName(speciesOf('tigerShark')),
  humpback: displayName(speciesOf('humpback')),
};

/** The islands' names as the player reads them. */
const ISLAND_NAMES: Readonly<Record<string, string>> = { maui: 'Maui', molokai: 'Molokaʻi', lanai: 'Lānaʻi', kahoolawe: 'Kahoʻolawe' };

/** How a death is told. */
const CAUSES: Readonly<Record<TagCause, string>> = {
  natural: 'died of natural causes',
  fished: 'was caught by the fishery',
  eaten: 'was eaten by a predator',
  crowded: 'died young, crowded by its own kind',
  'old age': 'died of old age, as every one of its kind does at its oldest',
};

/** An age told in years past one, in months below it. */
function age(years: number): string {
  const months = Math.max(1, Math.round(years * 12));
  return years >= 1 ? `${years.toFixed(1)} years` : `${months} ${months === 1 ? 'month' : 'months'}`;
}

/** The habitats a tag tells (M203), as the habitats' panel names them. */
const PLACE_NAMES: Readonly<Record<Exclude<TagPlace, 'away'>, string>> = {
  reef: 'Maui Nui Reef', slope: 'the Slope', blue: 'the Blue Water',
};

/** The habitats a tagged animal can be gone to, each drawn as a scene of its own (M203). */
const GONE_TO: readonly TagPlace[] = ['reef', 'slope', 'blue'];

/**
 * Where the Tagged list's way takes a tagged animal (M203): the habitat its tag has it in on `day`, or why it cannot go
 * there (M203's review: the button did nothing at a site visited or in the map view). A humpback is gone to nowhere: the
 * Blue Water draws one whale for each 1,000 in Hawaiʻi's waters.
 */
export function goToOf(world: World, id: number, day: number, how: { visiting: boolean; habitats: boolean }): { habitat: TagHabitat } | { why: string } {
  const a = world.tags.animals.find((t) => t.id === id);
  const at = a && a.species !== 'humpback' ? tagPlaceOn(world, a, day) : null;
  if (!at || !(TAG_HABITATS as readonly string[]).includes(at)) return { why: 'It is not in a habitat that can be shown today.' };
  if (how.visiting) return { why: 'Go back to your own sanctuary to go to it.' };
  if (!how.habitats) return { why: 'Show the aquarium as its habitats to go to it.' };
  return { habitat: at as TagHabitat };
}

/**
 * A tagged animal followed (`tag:<id>`) and not drawn in the habitat shown, `here`, on the day the scene shows, `day`
 * (M203's second review: the world's day, a relay ahead at speed, let it go): 'here' where its tag has it there still
 * (its drawing on its way), the habitat it is in instead where one can be followed to, else null (dead, away, or no
 * such tag).
 */
export function movedTo(world: World, id: string, here: string, day: number): TagHabitat | 'here' | null {
  const tag = /^tag:(\d+)$/.exec(id);
  const a = tag ? world.tags.animals.find((t) => t.id === Number(tag[1])) : undefined;
  const at = a ? tagPlaceOn(world, a, day) : null;
  if (at === here) return 'here';
  return at && (TAG_HABITATS as readonly string[]).includes(at) ? (at as TagHabitat) : null;
}

/**
 * Where a tagged animal is: a reef animal's island's reef; the others at Maui Nui (their habitat today, M203) or away
 * (the honu at French Frigate Shoals, a hawksbill over its breeding season, a humpback on its feeding grounds or the way
 * there).
 */
function where(r: TagRecord): string {
  if ('island' in r.where) return `${escapeHtml(ISLAND_NAMES[r.where.island] ?? r.where.island)}'s reef`;
  if (!r.where.away) {
    // A humpback's habitat not told nor gone to (M203's review: the Blue Water draws a whale for each 1,000 in Hawaiʻi's
    // waters, so its scene held none on 62 days of a year that a tagged whale's row sent the player there).
    const today = r.place && r.place !== 'away' && r.species !== 'humpback' ? `, ${PLACE_NAMES[r.place]} today` : '';
    return `${r.species === 'humpback' ? 'in Hawaiʻi\'s waters' : 'at Maui Nui'}${today}`;
  }
  return r.species === 'honu' ? 'away breeding at French Frigate Shoals' : r.species === 'honuea' ? 'away over its breeding season' : 'on its feeding grounds, or the way there';
}

function animal(r: TagRecord): string {
  const state = r.died ? `${escapeHtml(r.name)} ${CAUSES[r.died.cause]} on ${dateLabel(r.died.on)}, ${age(r.ageYears)} old` : `alive, ${age(r.ageYears)} old`;
  const price = r.price.usd === null ? `${r.price.kind}, its price not yet read` : `${r.price.kind}, $${whole(r.price.usd)} (not charged yet)`;
  return `<li class="tagged-animal${r.died ? ' dead' : ''}" data-tag="${r.id}"><h4>${escapeHtml(r.name)}</h4><dl>`
    + `<dt>Kind</dt><dd>${escapeHtml(TAG_SPECIES_NAMES[r.species])}${r.group ? `, of ${escapeHtml(r.group)}` : ''}</dd>`
    + `<dt>Where</dt><dd>${where(r)}</dd>`
    + `<dt>Now</dt><dd>${state}</dd>`
    + `<dt>Followed</dt><dd>${whole(r.daysTracked)} ${r.daysTracked === 1 ? 'day' : 'days'}, since ${dateLabel(r.taggedOn)}</dd>`
    + `<dt>Tag</dt><dd>${price}</dd></dl>`
    // A way to it where its habitat today is drawn (M203): the reef's stocks are marked over their island's schools.
    + (!r.died && !('island' in r.where) && r.species !== 'humpback' && r.place && GONE_TO.includes(r.place) ? `<button type="button" data-go="${r.id}">Go to it in ${PLACE_NAMES[r.place as Exclude<TagPlace, 'away'>]}</button>` : '')
    + '</li>';
}

function group(g: GroupRecord): string {
  const here = g.kind === 'school' && g.island !== null
    ? `${escapeHtml(ISLAND_NAMES[g.island] ?? g.island)}'s reef, ${whole(g.count)} of its grown fish there today`
    : `${whole(g.count)} of its tagged ${g.count === 1 ? 'member' : 'members'} living`;
  return `<li class="tagged-group" data-tag="${g.id}"><h4>${escapeHtml(g.name)}</h4><dl>`
    + `<dt>Kind</dt><dd>a ${g.kind} of ${escapeHtml(sentenceName(speciesOf(g.species), true))}</dd>`
    + `<dt>Where</dt><dd>${here}</dd>`
    + `<dt>Named</dt><dd>${dateLabel(g.taggedOn)}</dd></dl></li>`;
}

/** The Tagged list's markup: the animals, the living first, then the schools. */
export function taggedHtml(records: { animals: readonly TagRecord[]; groups: readonly GroupRecord[] }): string {
  const living = records.animals.filter((a) => !a.died);
  const dead = records.animals.filter((a) => a.died);
  if (records.animals.length === 0 && records.groups.length === 0) return '<h3>Tagged</h3><p>Nothing tagged yet. Tap an animal or a school in the aquarium or a habitat to tag and name it.</p>';
  return `<h3>Tagged</h3><p>${whole(living.length)} living, ${whole(dead.length)} dead, ${whole(records.groups.length)} ${records.groups.length === 1 ? 'group' : 'groups'} named.</p>`
    + `<ul class="tagged">${[...living, ...dead].map(animal).join('')}${records.groups.map(group).join('')}</ul>`;
}

/** A site's tags for its map card: the living, the dead and the schools; null where none is there. */
export function taggedAtSite(records: { animals: readonly TagRecord[]; groups: readonly GroupRecord[] }, siteId: string): string | null {
  const here = records.animals.filter((a) => a.siteId === siteId);
  const groups = records.groups.filter((g) => g.siteId === siteId).length;
  if (here.length === 0 && groups === 0) return null;
  const living = here.filter((a) => !a.died).length;
  const away = here.filter((a) => !a.died && 'away' in a.where && a.where.away).length;
  return `${whole(living)} tagged ${living === 1 ? 'animal' : 'animals'} living${away > 0 ? ` (${whole(away)} away)` : ''}, ${whole(here.length - living)} dead, ${whole(groups)} ${groups === 1 ? 'group' : 'groups'} named`;
}
